// Onboarding namespace — RUSSIAN (Module 02 §4). Composes the four screen
// namespaces the same way `onboardingEn` composes theirs, so the two trees stay
// structurally identical and `tsc` says so.
//
// `terms` — the base-label fallback for the 61 canonical term keys (§2.5) —
// duplicates `TERMINOLOGY_BASE` in packages/contracts, which is the real source
// of truth for the terminology LAYER. The duplication is not a choice: apps/web
// has no @assessly/contracts dependency (spec 06 deferred item 10), and adding
// one for a fallback string table is not this pass's call to make.
//
// So the copy is pinned instead of trusted. tools/ci/i18n-parity.ts — which
// already reaches into BOTH packages by relative path — asserts that every
// locale's `onboarding.terms.*` equals `TERMINOLOGY_BASE[key][locale]` exactly.
// If contracts changes a label and this file does not, the build fails. (That
// check found one drift the moment it was written: `cross_section` read
// "Cross-section" here and "Cross section" there.)

import type { Translated } from "../../i18n-shape.js";
import type { onboardingEn } from "./onboarding.i18n.js";
import { catalogueImportRu } from "./screens/catalogue-import.ru.js";
import { checklistScreenRu } from "./screens/checklist/checklist.ru.js";
import { plantMapRu } from "./screens/plant-map/plant-map.ru.js";
import { terminologySettingsRu } from "./screens/terminology/terminology.ru.js";
import { verticalRu } from "./screens/vertical/vertical.ru.js";
import { packUpdateRu } from "./screens/pack-update/pack-update.ru.js";

export const onboardingRuDict: Translated<typeof onboardingEn> = {
  ...catalogueImportRu,

  vertical: verticalRu,
  terminology: terminologySettingsRu,
  packUpdate: packUpdateRu,
  plant: plantMapRu,

  shell: {
    title: "Настройте свой завод",
    subtitle: "Четыре шага от пустого рабочего пространства до работающего завода.",
    stepRailLabel: "Шаги настройки",
    targetGoLive: "Плановая дата запуска",
    targetGoLiveUnset: "Не задана",
    liveBadge: "Запущен",
    liveSince: "Работает с {{date}}",
    frozenNotice:
      "Ваш завод запущен. Настройка сохраняется как запись; изменения вносите там, где работаете.",
    targetGoLiveSet: "Задать дату",
    targetGoLiveChange: "Изменить",
    targetGoLiveFailed: "Эту дату сохранить не удалось. Попробуйте ещё раз.",
    forbiddenTitle: "У вас нет доступа к настройке",
    forbiddenBody:
      "Настройкой завода занимается администратор вашей группы или площадки. Обратитесь к нему, если нужно что-то изменить.",
    loading: "Загружается сессия настройки…",
    errorTitle: "Не удалось загрузить сессию настройки",
    errorDetail: "Не удалось загрузить настройку. Попробуйте ещё раз.",
    retry: "Повторить",
    requestId: "Идентификатор запроса",
  },

  sessionStatus: {
    draft: "Не начато",
    in_progress: "В работе",
    ready_for_review: "Готово к проверке",
    live: "Запущен",
  },

  stepStatus: {
    not_started: "Не начат",
    in_progress: "В работе",
    completed: "Завершён",
  },

  steps: {
    label: "Шаг {{number}}",
    of: "Шаг {{number}} из {{total}}",
    vertical: {
      title: "Выберите отраслевой пакет",
      blurb:
        "Устанавливает операции, единицы измерения, проверки качества и терминологию вашей отрасли.",
    },
    catalogue: {
      title: "Загрузите каталог",
      blurb: "Продукты, материалы, спецификации и маршруты — вручную или из таблицы.",
    },
    plant: {
      title: "Составьте карту предприятия",
      blurb:
        "Площадки, подразделения, рабочие центры, места хранения и люди, которые всем этим управляют.",
    },
    opening: {
      title: "Внесите начальное состояние",
      blurb: "Начальные остатки, открытые заказы, клиенты и поставщики.",
    },
    quality: {
      title: "Проверьте контроль качества",
      blurb: "Подтвердите проверки из пакета или задайте свои допуски.",
    },
    unavailable: "Недоступно",
    unavailableHint:
      "Этот шаг откроется, когда будут включены нужные ему разделы Assessly. Сохранённое не пропадёт.",
    openStep: "Открыть",
  },
  qualityReview: {
    heading: "Контроль качества",
    intro: "Проверьте каждую точку, впишите значения вашего завода и подтвердите.",
    stageCount: "Проверок: {{count}}",
    pager: "Страницы раздела «{{stage}}»",
    pageRange: "{{from}}–{{to}} из {{total}}",
    prev: "Назад",
    next: "Вперёд",
    stage: {
      receiving: "Приёмка",
      in_process: "В процессе",
      final: "Финальный",
    },
    provenance: {
      pack: "Пакет v{{version}}",
      custom: "Ваша",
    },
    sampling: {
      every_unit: "Каждая единица",
      fixed_count: "Фиксированное число · {{n}}",
      percentage: "Выборка {{pct}}%",
      first_n: "Первые {{n}}",
      aql: "AQL {{level}}/{{value}} (приёмочный уровень)",
    },
    badges: {
      blocking: "Блокирующая",
      autoHold: "Авто-карантин при браке",
      inactive: "Отключена",
    },
    band: {
      min: "Мин",
      target: "Цель",
      max: "Макс",
      edit: "Изменить допуск",
      save: "Сохранить допуск",
      cancel: "Отмена",
      invalid: "Минимум не может превышать максимум, и все значения должны быть числами.",
    },
    ownership: {
      title: "Сделать проверку своей?",
      body: "После сохранения своих значений проверка становится вашей: обновления пакета больше её не изменят. Уже открытые инспекции сохраняют свою версию.",
      confirm: "Сохранить мои значения",
    },
    deactivate: {
      action: "Отключить",
      title: "Отключить проверку?",
      body: "Уже открытые инспекции сохраняют закреплённую версию, а переустановка пакета никогда не включит проверку обратно — это ваше решение, и оно сохраняется.",
      confirm: "Отключить проверку",
    },
    readOnly:
      "Вы можете просмотреть и подтвердить, но для изменения проверок нужно право на каталог качества — обратитесь к администратору группы.",
    forbiddenTitle: "Нет доступа к проверкам качества",
    forbiddenBody:
      "Для просмотра установленных проверок качества нужно право чтения каталога качества — попросите администратора группы выдать его или просмотреть этот шаг.",
    empty: {
      title: "Этот пакет не устанавливает проверок качества",
      body: "Подтвердите шаг, чтобы зафиксировать просмотр — свои проверки можно добавить позже в разделе «Качество».",
    },
    loadError: "Не удалось загрузить проверки качества.",
  },

  home: {
    resumeTitle: "Продолжите с того места, где остановились",
    resumeCta: "Вернуться к шагу {{number}}",
    resumeChecklistCta: "Просмотреть чек-лист запуска",
    allStepsDone: "Все четыре шага завершены. Просмотрите чек-лист и запускайтесь.",
    stepsHeading: "Ваши шаги",
    emptyTitle: "Давайте настроим ваш завод",
    emptyBody: "Начните с выбора отраслевого пакета, который подходит к тому, что вы производите.",
    emptyCta: "Выбрать отраслевой пакет",
    scoreHeading: "Полнота настройки",
    checklistLink: "Открыть чек-лист запуска",
    settingsLink: "Настройки терминологии",
    homeLink: "Обзор настройки",
  },

  score: {
    label: "Показатель полноты",
    value: "{{score}} из 100",
    threshold: "Для запуска нужно {{threshold}}",
    // Как и в английском оригинале, здесь намеренно нет суффиксов множественного
    // числа i18next: у русского три формы вместо двух английских, а ключи у всех
    // локалей общие (D-030). Поэтому число всегда стоит после двоеточия —
    // конструкция верна и для 1, и для 2, и для 5.
    blockingUnmet: "Осталось блокирующих пунктов: {{count}}",
    blockingClear: "Блокирующих пунктов не осталось",
    neverEvaluated: "Ещё не оценивалось",
  },

  goLive: {
    cta: "Запустить",
    working: "Запускаем…",
    confirmTitle: "Запустить ваш завод?",
    confirmBody:
      "Запуск завершает настройку: эти шаги сохраняются как запись, и завод начинает работу.",
    confirm: "Да, запускаем",
    cancel: "Отмена",
    doneTitle: "Вы запущены",
    doneBody: "Настройка завершена с показателем полноты {{score}}.",
    blockedTitle: "К запуску пока не готовы",
    blockedSteps: "Эти шаги ещё не завершены: {{steps}}.",
    blockedItems: "Эти блокирующие пункты чек-листа не пройдены и не закрыты отступлением:",
    blockedScore: "Полнота — {{score}}; требуется {{required}} (не хватает {{gap}}).",
    blockedPermission: "Запустить завод может только администратор группы.",
    alreadyLive: "Ваш завод уже запущен.",
    busy: "Выполняется другое изменение настройки. Повторите через мгновение.",
    failedTitle: "В запуске отказано",
  },

  save: {
    save: "Сохранить",
    saving: "Сохраняется…",
    saved: "Сохранено",
    saveAndContinue: "Сохранить и продолжить",
    completeStep: "Отметить шаг завершённым",
    completing: "Завершается…",
    completed: "Шаг завершён",
    unsaved: "Есть несохранённые изменения.",
    leaveTitle: "Уйти без сохранения?",
    leaveBody: "На этом шаге есть изменения, которые не сохранены. Если уйти сейчас, они пропадут.",
    leaveDiscard: "Уйти без сохранения",
    leaveStay: "Остаться на шаге",
    readOnly: "У вас нет прав изменять этот шаг.",
    failedTitle: "Не удалось сохранить этот шаг",
    failedDetail: "Ваши изменения не сохранены. Попробуйте ещё раз.",
  },

  conflict: {
    staleTitle: "Этот шаг изменил кто-то другой",
    staleBody:
      "Пока вы правили, шаг сохранил кто-то другой. Загрузите их версию, затем примените свои изменения заново.",
    staleReload: "Загрузить текущую версию",
    staleSavedAt: "Сохранено {{time}}",
    busyTitle: "Настройка занята",
    busyBody: "Выполняется другое изменение настройки. Повторите через мгновение.",
    busyRetry: "Попробовать снова",
    alreadyLiveTitle: "Ваш завод уже запущен",
    alreadyLiveBody:
      "Настройка теперь сохраняется как запись. Изменения вносите на тех экранах, где вы работаете.",
  },

  preconditions: {
    title: "Сначала сделайте это",
    body: "Этот шаг нельзя завершить, пока не сделано следующее:",
    stepNote: "Шаг {{number}} завершить не удалось.",
  },

  precondition: {
    vertical_pack_installed: "Установите отраслевой пакет",
    all_steps_completed: "Завершите все четыре шага настройки",
  },

  checklist: {
    title: "Чек-лист запуска",
    blurb: "Блокирующие проверки должны пройти до запуска.",
    open: "Открыть чек-лист",
    categories: {
      vertical: "Отраслевой пакет",
      quality: "Качество", // D-214
      catalogue: "Каталог",
      plant: "Предприятие",
      people: "Сотрудники",
      opening: "Начальное состояние",
      channels: "Каналы связи",
    },
    itemStatus: {
      pending: "Не проверено",
      passed: "Пройдено",
      failed: "Не выполнено",
      waived: "Отступление",
    },
    blocking: "Блокирующая",

    ...checklistScreenRu,
  },

  dialog: {
    cancel: "Отмена",
  },

  opening: {
    intro: "С чего завод начинает: {{openingStock}}, клиенты и открытые заказы.",
    scopeTitle: "Что можно загрузить сегодня",
    scopeBody:
      "Каждое направление загружается из таблицы. Клиенты и открытые заказы попадают прямо в продажи.",
    asOfLabel: "Остатки по состоянию на",
    asOfHint:
      "Бизнес-дата, на которую сняты ваши начальные цифры. Сохраняется вместе с шагом; сама по себе ничего не проводит.",
    tabs: {
      label: "Начальное состояние",
      stock: "Остатки",
      commercial: "Клиенты и поставщики",
      orders: "Открытые заказы",
    },
  },

  screens: {
    home: "Настройте свой завод",
    vertical: "Выберите отраслевой пакет",
    catalogue: "Загрузите каталог",
    importDetail: "Загрузка",
    plant: "Составьте карту предприятия",
    checklist: "Чек-лист запуска",
    terminology: "Терминология",
  },

  state: {
    loading: "Загрузка…",
    errorTitle: "Что-то пошло не так",
    errorDetail: "Запрос выполнить не удалось. Попробуйте ещё раз.",
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    empty: "Здесь пока пусто.",
    notPermitted: "У вас нет к этому доступа.",
  },

  // Verbatim from TERMINOLOGY_BASE[key].ru (packages/contracts) — gate-pinned.
  terms: {
    work_order: "Заказ на производство",
    work_center: "Рабочий центр",
    routing_step: "Операция маршрута",
    stock_unit: "Складская единица",
    lot: "Партия",
    remnant: "Остаток",
    off_cut: "Обрезок",
    waste: "Отходы",
    scrap: "Брак",
    rework: "Доработка",
    shift: "Смена",
    department: "Подразделение",
    facility: "Площадка",
    machine: "Станок",
    operator: "Оператор",
    quote: "Коммерческое предложение",
    sales_order: "Заказ на продажу",
    purchase_order: "Заказ на закупку",
    supplier: "Поставщик",
    customer: "Клиент",
    bom: "Спецификация",
    product: "Продукт",
    material: "Материал",
    opening_stock: "Начальные остатки",
    pull_test: "Испытание на разрыв",
    quality_check: "Контроль качества",
    downtime: "Простой",
    setup: "Наладка",
    teardown: "Разборка",
    barrel: "Барабан",
    bobbin: "Бобина",
    batch: "Серия",
    planned_order: "Плановый заказ",
    promise_date: "Дата поставки",
    drawing: "Волочение",
    twisting: "Скрутка",
    coating: "Покрытие",
    remaining_length: "Остаточная длина",
    measured_length: "Измеренная длина",
    claimed_length: "Заявленная длина",
    calibration_variance: "Отклонение калибровки",
    location: "Место хранения",
    warehouse: "Склад",
    transfer: "Перемещение",
    cycle_count: "Циклический пересчёт",
    defect: "Дефект",
    hold: "Блокировка",
    release: "Разблокировка",
    co_product: "Сопутствующий продукт",
    by_product: "Побочный продукт",
    regrind: "Вторичный материал",
    pellet: "Гранулят",
    extrusion: "Экструзия",
    armoring: "Бронирование",
    sheathing: "Оболочка",
    engraving: "Гравировка",
    cross_section: "Сечение",
    core_count: "Число жил",
    voltage_rating: "Номинальное напряжение",
    color_code: "Цветовой код",
    price_tier: "Ценовой уровень",
  },
};
